/**
 * The save path of a drag and drop (#2038). The drop first runs check-conflicts once; when the
 * response carries warning lines (#2041: the employee's availability; #2042: day, week and
 * rest time against the org's shift settings), one dialog lists them
 * and the planner moves anyway or cancels. A failed check moves without it. A series member
 * then asks the series scope
 * (only "this occurrence" can move to another day or employee: the series route changes
 * times and groups, not dates). A PUBLISHED or CONFIRMED shift then asks the published-shift
 * confirmation, because the employee gets a shift_changed push. The save is the ordinary
 * PUT /shifts/{id}: a 409 (overlap, absence, stale version) shows the usual error toast and
 * the block stays where it was.
 */
import { useCallback, useState } from 'react';
import { useLingui } from '@lingui/react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useCheckShiftConflicts, useUpdateShift } from '@/hooks/useShifts';
import { toEnumMember } from '@/lib/enumMember';
import { shifts_UpdateShiftRequest } from '@/api/models/shifts_UpdateShiftRequest';
import type { Shift, UpdateShiftRequest } from '@/types/shifts';
import { ShiftStatus } from '@/types/shifts';
import type { ViewMode } from '@/utils/shiftHelpers';
import { planShiftDrop, type ShiftDropTarget, type ShiftMove } from './shiftDragDrop';
import { ShiftWarningsDialog, conflictWarningLines } from './ShiftWarningLines';

/**
 * The PUT body of a move: the stored shift with the new day and employee, nothing else. An
 * open shift (#2040) moved to another day stays open.
 */
export function buildMoveRequest(shift: Shift, move: ShiftMove): UpdateShiftRequest {
  return {
    employee_id: move.employeeId || undefined,
    open_shift: move.employeeId ? undefined : true,
    shift_date: move.shiftDate,
    start_time: shift.start_time ?? '',
    end_time: shift.end_time ?? '',
    break_minutes: shift.break_minutes,
    break_start: shift.break_start || undefined,
    break_end: shift.break_end || undefined,
    shift_type:
      toEnumMember(shifts_UpdateShiftRequest.shift_type, shift.shift_type) ??
      shifts_UpdateShiftRequest.shift_type.REGULAR,
    status:
      toEnumMember(shifts_UpdateShiftRequest.status, shift.status) ?? shifts_UpdateShiftRequest.status.DRAFT,
    group_ids: (shift.groups ?? []).map((g) => g.id).filter((id): id is string => !!id),
    notes: shift.notes || undefined,
    current_updated_at: shift.updated_at ?? '',
  };
}

type Pending = { shift: Shift; move: ShiftMove; step: 'warnings' | 'series' | 'published'; lines?: string[] };

function isSeriesMember(shift: Shift): boolean {
  return !!shift.pattern_id && !shift.is_exception;
}

function isPublished(shift: Shift): boolean {
  return shift.status === ShiftStatus.PUBLISHED || shift.status === ShiftStatus.CONFIRMED;
}

/**
 * Drop handler and the dialogs it may open; render `dialogs` once in the page.
 * `employeeName` names the target employee in the warning lines.
 */
export function useShiftMove(viewMode: ViewMode, employeeName: (employeeId: string) => string = () => '') {
  const { i18n } = useLingui();
  const updateShift = useUpdateShift();
  const [pending, setPending] = useState<Pending | null>(null);

  const save = useCallback(
    async (shift: Shift, move: ShiftMove) => {
      setPending(null);
      if (!shift.id) return;
      // The hook shows the error toast; the grid never moved the block itself.
      await updateShift.mutateAsync({ id: shift.id, request: buildMoveRequest(shift, move) }).catch(() => undefined);
    },
    [updateShift]
  );

  const proceed = useCallback(
    (shift: Shift, move: ShiftMove, after: 'series' | 'none') => {
      if (after === 'none' && isSeriesMember(shift)) {
        setPending({ shift, move, step: 'series' });
      } else if (isPublished(shift)) {
        setPending({ shift, move, step: 'published' });
      } else {
        void save(shift, move);
      }
    },
    [save]
  );

  const { mutateAsync: checkConflictsAsync } = useCheckShiftConflicts();

  const runDrop = useCallback(
    async (shift: Shift, target: ShiftDropTarget) => {
      const move = planShiftDrop(shift, target, viewMode);
      if (!move) return;
      let lines: string[] = [];
      if (shift.start_time && shift.end_time) {
        try {
          const response = await checkConflictsAsync({
            // An open shift (#2040) is checked as open: no overlap, the day limit only.
            ...(move.employeeId ? { employee_id: move.employeeId } : { open_shift: true }),
            shift_date: move.shiftDate,
            start_time: shift.start_time,
            end_time: shift.end_time,
            exclude_id: shift.id,
            // The break feeds the day and week warnings (#2042).
            break_minutes: shift.break_minutes,
          });
          lines = conflictWarningLines(response, employeeName(move.employeeId), i18n);
        } catch {
          // A failed check moves without warnings; the save itself still validates.
        }
      }
      if (lines.length > 0) {
        setPending({ shift, move, step: 'warnings', lines });
      } else {
        proceed(shift, move, 'none');
      }
    },
    [viewMode, proceed, employeeName, i18n, checkConflictsAsync]
  );

  const onShiftDrop = useCallback(
    (shift: Shift, target: ShiftDropTarget) => {
      void runDrop(shift, target);
    },
    [runDrop]
  );

  // Closing a dialog clears only its own step: the series action already moved on to the
  // published step when Radix reports the close.
  const closeStep = (step: Pending['step']) => setPending((p) => (p?.step === step ? null : p));

  const dialogs = (
    <>
      <ShiftWarningsDialog
        open={pending?.step === 'warnings'}
        lines={pending?.lines ?? []}
        onConfirm={() => pending && proceed(pending.shift, pending.move, 'none')}
        onCancel={() => closeStep('warnings')}
      />

      <AlertDialog open={pending?.step === 'series'} onOpenChange={(open) => !open && closeStep('series')}>
        <AlertDialogContent data-testid="move-series-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {i18n._({ id: 'shifts.dnd.seriesTitle', message: 'Move a shift of a series' })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {i18n._({
                id: 'shifts.dnd.seriesMessage',
                message:
                  'Only this occurrence moves. The rest of the series stays as it is, and this shift is marked as individually changed.',
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{i18n._({ id: 'common.cancel', message: 'Cancel' })}</AlertDialogCancel>
            <AlertDialogAction
              data-testid="move-series-this-only"
              onClick={() => pending && proceed(pending.shift, pending.move, 'series')}
            >
              {i18n._({ id: 'shifts.series.updateChoice.thisOnly', message: 'This occurrence only' })}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={pending?.step === 'published'} onOpenChange={(open) => !open && closeStep('published')}>
        <AlertDialogContent data-testid="move-published-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {i18n._({ id: 'shifts.edit.publishedWarning.title', message: 'Published Shift' })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {i18n._({
                id: 'shifts.dnd.publishedMessage',
                message:
                  'The employee sees this shift in the app. The move sends a notification, and a confirmed shift must be confirmed again.',
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{i18n._({ id: 'common.cancel', message: 'Cancel' })}</AlertDialogCancel>
            <AlertDialogAction
              data-testid="move-published-confirm"
              onClick={() => pending && void save(pending.shift, pending.move)}
            >
              {i18n._({ id: 'shifts.dnd.move', message: 'Move' })}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );

  return { onShiftDrop, dialogs, isSaving: updateShift.isPending };
}
